import type { FuelCardVehicle, FuelTransactionBodyContent } from '@cobliteam/common-types';
import type { Configuration } from '../..';
import HttpMethod from '../../request/HttpMethod';
import Requester from '../../request/Requester';
import type { IRequest } from '../../request/Types';
import { fuelVehiclesTransformer, openNewFuelCardTicketToOpenNewFuelCardTicketAPI, toSaveFuelTransactionBody } from './APITransformers';
import type { CardProviderOption, ZendeskTicket } from './Definitions/Types';

export class FuelCardRequester {
  private requester: Requester;

  constructor(configuration: Configuration) {
    this.requester = new Requester(configuration);
  }

  public saveFuelTransaction = (body: FuelTransactionBodyContent): Promise<void> => {
    const transactionBody = toSaveFuelTransactionBody(body);
    const requestData: IRequest = {
      endpoint: 'fuel/transactions',
      method: HttpMethod.POST,
      body: JSON.stringify(transactionBody),
    };

    return this.requester.request(requestData).then();
  };

  public getFuelVehicles = (): Promise<FuelCardVehicle[]> => {
    const requestData: IRequest = {
      endpoint: '/fuel/vehicles',
      method: HttpMethod.GET,
    };

    return this.requester.request(requestData).then((response) => fuelVehiclesTransformer(response.data));
  };

  public updateFuelVehicles = (vehicles: FuelCardVehicle[]): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/vehicles',
      method: HttpMethod.PATCH,
      body: JSON.stringify(
        vehicles.map(({ id, refConsumption, integrationId }) => ({
          id,
          ref_fuel_consumption: refConsumption,
          integration_id: integrationId,
        }))
      ),
    };

    return this.requester.request(requestData).then();
  };

  public requestIntegration = (cardProvider: CardProviderOption, cnpj?: string, email?: string): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/fuel-card-integration',
      method: HttpMethod.POST,
      body: JSON.stringify({ card_provider: cardProvider, cnpj, email }),
    };

    return this.requester.request(requestData).then();
  };

  public updateManualFeatureFlag = (): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/featureFlag',
      method: HttpMethod.PATCH,
      body: JSON.stringify({ flag_name: 'fuel-card-manual' }),
    };

    return this.requester.request(requestData).then();
  };

  public openNewFuelCardTicket = (zendeskTicket: ZendeskTicket): Promise<void> => {
    const requestData: IRequest = {
      endpoint: '/fuel/fuelcard/ticket',
      method: HttpMethod.POST,
      body: JSON.stringify(openNewFuelCardTicketToOpenNewFuelCardTicketAPI(zendeskTicket)),
    };

    return this.requester.request(requestData).then();
  };
}

export default FuelCardRequester;
